import { useCallback, useEffect, useState } from 'react';
import { isEqual } from 'lodash';
import {
  ApplicableFiltersInterface,
  ApplicableFiltersStateManagement,
} from '../components/search/admin-dashboard-tables/interfaces';

/**
 * This hook tracks Algolia facet and numeric filters, using an internal map-state
 * that does not trigger a new search until `applyFilters` is called.
 * This means that a collection of different filters can be set before a search
 * is made. In this way, we can save queries to Algolia and prevent superfluous
 * re-renders.
 * @param initialRefinementList
 * @param initialNumericFilters
 * @return {Object}
 */
const useApplicableFilters = (
  initialRefinementList: {[key: string]: string[]},
  initialNumericFilters: (string|string[])[],
): ApplicableFiltersStateManagement => {
  /**
   * Internal state, used to track numeric filters.
   * Because numeric filters are difficult to track via a simple keyed object,
   * we track them separately.
   */
  const [numericFilters, setNumericFilters] = useState(initialNumericFilters);

  /**
   * Internal map state, used to track facet values for an attribute without updating Algolia
   */
  const [pendingFiltersMap, setPendingFiltersMap] = useState<ApplicableFiltersInterface>({
    refinementList: initialRefinementList,
    numericFilters,
  });

  /**
   * Internal map state, used to map attributes already applied,
   *   this is used to track whether there are pending changes.
   */
  const [filtersListToApply, setFiltersListToApply] = useState(pendingFiltersMap);

  /**
   * Simple boolean that indicates whether there are changes waiting to be applied to attributes.
   */
  const [isDirty, setIsDirty] = useState(false);

  /**
   * This effect updates isDirty state by comparing the list of filters pending application
   * to those that have been applied.
   */
  useEffect(() => {
    const pendingChanges = !isEqual(filtersListToApply, pendingFiltersMap);
    if (pendingChanges) {
      setIsDirty(true);
    }
    if (!pendingChanges) {
      setIsDirty(false);
    }
  }, [pendingFiltersMap, filtersListToApply]);

  /**
   * This effect adds numericFilters to the list of mapped filters that are pending application.
   */
  useEffect(() => {
    setPendingFiltersMap((currentMappedAttributes) => ({
      ...currentMappedAttributes,
      numericFilters,
    }));
  }, [numericFilters]);

  /**
   * This method will update the refinementList filters that are tracked via
   * pendingFiltersMap and are pending application; it will not apply filters.
   */
  const updatePendingRefinements = useCallback((
    attribute: string,
    eventValue?: string[],
  ) => {
    setPendingFiltersMap((currentMappedAttributes) => ({
      ...currentMappedAttributes,
      refinementList: {
        ...currentMappedAttributes.refinementList,
        [attribute]: eventValue || [],
      },
    }));
  }, []);

  /**
   * This method will apply currently mapped filters that are awaiting application.
   */
  const applyFilters = useCallback(() => {
    setFiltersListToApply(pendingFiltersMap);
  }, [pendingFiltersMap]);

  /**
   * This method will reset filters to a passed state. There may be times when
   * certain filters are meant to be persisted, despite a "reset" and so this
   * method accepts an externally defined "preset" state to which to revert
   * filters.
   */
  const resetFilters = useCallback((newAttributes: ApplicableFiltersInterface) => {
    setFiltersListToApply(newAttributes);
    setPendingFiltersMap(newAttributes);
  }, []);

  /**
   * This method removes a currently selected attribute from mapped state
   * and immediately applies changes.
   */
  const removeSelectedAttributeValue = useCallback((attribute: string, value: string) => {
    // Because React state setting is async, we need to set both pending
    // and applied changes separately.
    setPendingFiltersMap((currentMappedAttributes) => {
      const newFilters = { ...currentMappedAttributes.refinementList };
      newFilters[attribute] = newFilters[attribute].filter((currentVal) => currentVal !== value);
      return {
        ...currentMappedAttributes,
        refinementList: newFilters,
      };
    });
    setFiltersListToApply((currentlyAppliedAttributes) => {
      const newFilters = { ...currentlyAppliedAttributes.refinementList };
      newFilters[attribute] = newFilters[attribute].filter((currentVal) => currentVal !== value);
      return {
        ...currentlyAppliedAttributes,
        refinementList: newFilters,
      };
    });
  }, []);

  /**
   * This method updates an attribute value in mapped state and immediately applies changes.
   */
  const addSelectedAttributeValue = useCallback((attribute: string, value: string[]) => {
    // Because React state setting is async, we need to set both pending
    // and applied changes separately.
    setPendingFiltersMap((currentMappedAttributes) => {
      return {
        ...currentMappedAttributes,
        refinementList: {
          ...currentMappedAttributes.refinementList,
          [attribute]: value,
        },
      };
    });
    setFiltersListToApply((currentlyAppliedAttributes) => {
      return {
        ...currentlyAppliedAttributes,
        refinementList: {
          ...currentlyAppliedAttributes.refinementList,
          [attribute]: value,
        },
      };
    });
  }, []);

  /**
   * This method clears all filter options that have been queued for application,
   * but have not yet been applied.
   */
  const clearQueuedOptionsFromPendingFilters = useCallback(() => {
    setPendingFiltersMap(filtersListToApply);
  }, [filtersListToApply]);

  return {
    applyFilters,
    clearQueuedOptionsFromPendingFilters,
    filtersListToApply,
    isDirty,
    removeSelectedAttributeValue,
    addSelectedAttributeValue,
    resetFilters,
    setNumericFilters,
    updatePendingRefinements,
  };
};

export default useApplicableFilters;
